import * as React from "react";
import { useState, useEffect, useContext } from "react";
import {
  useListContext,
  DataProviderContext,
  useRefresh,
  useGetList,
  useNotify,
  Confirm,
} from "react-admin";
import { Box, Typography } from "@mui/material";
import { DragDropContext } from "@hello-pangea/dnd";
import isEqual from "lodash/isEqual";
import { stages } from "./stages";
import RewardColumn from "./RewardColumn";
import { REWARD_STATUS } from "../enums/RewardStatus";

const initialRewards = stages.reduce(
  (obj, stage) => ({ ...obj, [stage]: [] }),
  {}
);

const getRewardsByColumn = (data) => {
  // Group rewards by column
  const columns = data?.reduce(
    (acc, record) => {
      acc[record.status].push(record.id);
      return acc;
    },
    stages.reduce((obj, stage) => ({ ...obj, [stage]: [] }), {})
  );

  // Order each column by index
  stages?.forEach((stage) => {
    columns[stage] = columns[stage].sort(
      (a, b) =>
        data.find((d) => d._id === a).updatedAt -
        data.find((d) => d._id === b).updatedAt
    );
  });
  return columns;
};

const RewardKanbanContent = () => {
  const { filterValues, setFilters } = useListContext();
  const refresh = useRefresh();
  const notify = useNotify();
  const dataProvider = useContext(DataProviderContext);
  const [showConfirm, setShowConfirm] = useState(false);
  const [rewardToDeliver, setRewardToDeliver] = useState();
  const [dragResult, setDragResult] = useState();

  const { data, isPending } = useGetList("redeemed-rewards", {
    pagination: { page: 1, perPage: 10000 }, // FIXME: get all pages at once?!
    sort: { field: "updatedAt", order: "DESC" },
    filter: filterValues,
  });

  const [rewards, setRewards] = useState(
    !isPending ? getRewardsByColumn(data) : initialRewards
  );

  useEffect(() => {
    if (isPending) return;
    const newRewards = getRewardsByColumn(data);
    if (isEqual(rewards, newRewards)) {
      return;
    }
    setRewards(newRewards);
  }, [data, isPending]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDialogClose = () => {
    const { destination, source, draggableId } = dragResult;
    const sourceColumn = Array.from(rewards[source.droppableId]);
    const destinationColumn = Array.from(rewards[destination.droppableId]);

    // Revert local state
    sourceColumn.splice(source.index, 0, draggableId);
    destinationColumn.splice(destination.index, 1);

    setRewards({
      ...rewards,
      [source.droppableId]: sourceColumn,
      [destination.droppableId]: destinationColumn,
    });

    setShowConfirm(false);
    setRewardToDeliver(null);
    setDragResult(null);
  };

  const handleConfirm = async () => {
    setShowConfirm(false);
    if (!rewardToDeliver) return;

    // Deliver reward
    try {
      const deliveredReward = await dataProvider.deliverReward(
        "redeemed-rewards",
        { id: rewardToDeliver._id, deliveryCode: rewardToDeliver.deliveryCode }
      );
      if (deliveredReward) {
        notify("¡Premio entregado con éxito!", {
          type: "success",
        });
        setFilters({ ...filterValues, deliveryCode: "" }, [], true);
      }
    } catch (error) {
      notify("Ha ocurrido un error al entregar el premio", { type: "error" });
    } finally {
      setRewardToDeliver(null);
      setDragResult(null);
      refresh();
    }
  };

  const onDragEnd = async (result) => {
    const { destination, source, draggableId } = result;

    // Disable moving from Delivered to Pending Delivery
    if (
      !destination ||
      (source.droppableId === REWARD_STATUS.DELIVERED &&
        destination.droppableId === REWARD_STATUS.PENDING_DELIVERY)
    ) {
      return;
    }

    // Disable moving in the same column
    if (source.droppableId === destination.droppableId) return;

    // Validate delivery code
    const sourceColumn = Array.from(rewards[source.droppableId]);
    const destinationColumn = Array.from(rewards[destination.droppableId]);
    const sourceReward = data.find((d) => d._id === sourceColumn[source.index]);

    if (sourceReward.deliveryCode && !filterValues.deliveryCode) {
      notify(
        "Escribe el código de 6 dígitos que te indique el cliente (paso 1) antes de pasar un premio de Recibido a Entregado.",
        {
          type: "error",
        }
      );
      return;
    }

    // Update local state
    sourceColumn.splice(source.index, 1);
    destinationColumn.splice(destination.index, 0, draggableId);
    setRewards({
      ...rewards,
      [source.droppableId]: sourceColumn,
      [destination.droppableId]: destinationColumn,
    });

    setShowConfirm(true);
    setDragResult(result);
    setRewardToDeliver(sourceReward);
  };

  if (isPending) return null;

  return (
    <>
      <h3 style={{ marginLeft: 16, paddingTop: 16 }}>
        2. Arrastrá el premio de Recibido a Entregado:
      </h3>
      <Typography color="error" sx={{ ml: 2 }}>
        No olvides <b>marcar el premio como entregado</b>.
      </Typography>
      <DragDropContext onDragEnd={onDragEnd}>
        <Box sx={{ display: "flex" }}>
          {stages.map((stage) => (
            <RewardColumn
              stage={stage}
              rewardIds={rewards[stage]}
              data={data}
              key={stage}
            />
          ))}
        </Box>
      </DragDropContext>
      <Confirm
        isOpen={showConfirm}
        loading={isPending}
        title={`Entregar premio`}
        content="¿Estás seguro de entregar este premio?"
        onConfirm={handleConfirm}
        onClose={handleDialogClose}
      />
    </>
  );
};

export default RewardKanbanContent;
